"use client";

import { Button } from "@pex/ui-web";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { FaqAccordion } from "@/components/faq-accordion";
import { ScrollLink } from "@/components/scroll-link";
import { AnimatedStat } from "../../about/_sections/animated-stat";
import { DonationForm } from "./donation-form";

// ── Section 1: Hero ───────────────────────────────────────────────────────────

const HERO_PHOTOS_ROW1 = [
  { src: "/images/donate/Donate_01.webp", alt: "" },
  { src: "/images/donate/Donate_02.webp", alt: "" },
  { src: "/images/donate/Donate_03.webp", alt: "" },
  { src: "/images/donate/Donate_04.webp", alt: "" },
  { src: "/images/donate/Donate_05.webp", alt: "" },
  { src: "/images/donate/Donate_06.webp", alt: "" },
] as const;

const HERO_PHOTOS_ROW2 = [
  { src: "/images/donate/Donate_07.webp", alt: "" },
  { src: "/images/donate/Donate_08.webp", alt: "" },
  { src: "/images/donate/Donate_09.webp", alt: "" },
  { src: "/images/donate/Donate_10.webp", alt: "" },
  { src: "/images/donate/Donate_11.webp", alt: "" },
  { src: "/images/donate/Donate_12.webp", alt: "" },
] as const;

function DonateHero() {
  return (
    <section className="pt-20 pb-0">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading + body + CTAs */}
        <div className="mx-auto max-w-2xl space-y-6 text-center">
          <h1 className="heading-1 font-bold">
            Make a Way for <span className="font-serif italic">Change</span>
          </h1>
          <p className="body-lg text-muted-foreground">
            90% of people in need of addiction treatment never receive the help
            that they need. Your investment into Project Exodus helps to
            establish accessible, community-based recovery services where they
            are needed most.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild variant="fancy">
              <ScrollLink href="#donate">Donate Now</ScrollLink>
            </Button>
            <Button asChild variant="secondary-fancy">
              <ScrollLink href="#impact">Learn More</ScrollLink>
            </Button>
          </div>
        </div>
      </div>

      {/* Dual-direction marquee — no edge fades, images run full-bleed */}
      <div aria-hidden="true" className="mt-14 space-y-2 overflow-hidden">
        {/* Row 1 — scrolls left; pointer-events-none prevents CSS :hover pause */}
        <div
          className="pointer-events-none flex w-max animate-marquee gap-3"
          style={{ animationDuration: "60s" }}
        >
          {[...HERO_PHOTOS_ROW1, ...HERO_PHOTOS_ROW1].map((photo, i) => (
            <div
              className="relative h-[220px] w-[295px] shrink-0 overflow-hidden rounded-xl sm:h-[260px] sm:w-[346px]"
              // biome-ignore lint/suspicious/noArrayIndexKey: static decorative duplicate list
              key={i}
            >
              <Image
                alt={photo.alt}
                className="object-cover"
                fill
                sizes="346px"
                src={photo.src}
              />
            </div>
          ))}
        </div>
        {/* Row 2 — scrolls right; pointer-events-none prevents CSS :hover pause */}
        <div
          className="pointer-events-none flex w-max animate-marquee-reverse gap-3"
          style={{ animationDuration: "60s" }}
        >
          {[...HERO_PHOTOS_ROW2, ...HERO_PHOTOS_ROW2].map((photo, i) => (
            <div
              className="relative h-[220px] w-[295px] shrink-0 overflow-hidden rounded-xl sm:h-[260px] sm:w-[346px]"
              // biome-ignore lint/suspicious/noArrayIndexKey: static decorative duplicate list
              key={i}
            >
              <Image
                alt={photo.alt}
                className="object-cover"
                fill
                sizes="346px"
                src={photo.src}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Section 2: Impact Stats ───────────────────────────────────────────────────

const DONATE_STATS = [
  { number: "3300+", label: "group facilitators trained." },
  { number: "110+", label: "recovery groups launched internationally." },
  { number: "2700+", label: "beneficiaries per week." },
  { number: "250+", label: "recovery resources developed." },
] as const;

function DonateImpact() {
  return (
    <section className="py-28" id="impact">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 md:items-start md:gap-16">
          {/* Left — heading only */}
          <div>
            <h2 className="heading-2 font-bold">
              The Impact of Your Contribution
            </h2>
          </div>

          {/* Right — body stacked above 2×2 stat grid */}
          <div className="space-y-8">
            <p className="body-lg text-muted-foreground">
              Your generosity helps us to scale our reach. We use financial
              contributions to{" "}
              <strong className="text-foreground">
                fund programme development
              </strong>
              ,{" "}
              <strong className="text-foreground">
                deliver recovery resources
              </strong>
              , and{" "}
              <strong className="text-foreground">
                train group facilitators
              </strong>{" "}
              across South Africa and beyond.
            </p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-10">
              {DONATE_STATS.map((stat) => (
                <AnimatedStat
                  key={stat.number}
                  label={stat.label}
                  number={stat.number}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Section 3: Tax Benefits ───────────────────────────────────────────────────

function DonateTaxBenefits() {
  return (
    <section className="py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-2 md:items-center md:gap-16">
          {/* Left — image: aspect-square matches Webflow's square source format */}
          <div className="relative aspect-square overflow-hidden rounded-2xl">
            <Image
              alt="A smiling man wearing a hat in warm sunlight."
              className="object-cover"
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              src={"/images/donate/Donate_LeftImage01.webp"}
            />
          </div>

          {/* Right — heading + body + CTAs */}
          <div className="space-y-6">
            <h2 className="heading-2 font-bold">
              Maximise Your Donation with Tax Benefits
            </h2>
            <p className="body-lg text-muted-foreground">
              Donations to Project Exodus qualify for{" "}
              <strong className="text-foreground">
                Section 18A tax deductions in South Africa
              </strong>
              , making your contribution even more impactful. This means that
              not only do you support vital recovery services, but you also
              benefit from tax relief.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild variant="secondary-hybrid">
                <ScrollLink href="#donate">Donate</ScrollLink>
              </Button>
              <Button asChild variant="link-icon">
                <Link href="/contact">
                  Contact
                  <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Section 4: Donation Widget ────────────────────────────────────────────────

function DonateDonationWidget() {
  return (
    <section className="relative overflow-hidden py-28" id="donate">
      {/* Teal/blue radial gradient — matches the StatsSection treatment */}
      <div className="pointer-events-none absolute inset-0 z-0 select-none">
        <div
          className="relative h-full w-full"
          style={{
            maskImage:
              "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)",
          }}
        >
          <div
            className="absolute -bottom-[20%] -left-[10%] h-[70%] w-[80%] rounded-[100%] opacity-70"
            style={{ background: "#D0EEF1", filter: "blur(100px)" }}
          />
          <div
            className="absolute -top-[10%] -right-[10%] h-[80%] w-[60%] rounded-[100%] opacity-50"
            style={{ background: "#C8DAEF", filter: "blur(90px)" }}
          />
          <div
            className="absolute top-[20%] left-[25%] h-[50%] w-[50%] rounded-[100%] opacity-35 mix-blend-screen"
            style={{ background: "#F0FAFB", filter: "blur(80px)" }}
          />
          <div
            className="absolute inset-0 opacity-20 mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              maskImage:
                "radial-gradient(circle at 50% 50%, black, transparent 80%)",
              WebkitMaskImage:
                "radial-gradient(circle at 50% 50%, black, transparent 80%)",
            }}
          />
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-xl space-y-8 text-center">
          <div className="space-y-3">
            <h2 className="heading-2 font-bold">Write the Next Chapter</h2>
            <p className="body-lg text-muted-foreground">
              Choose how you&apos;d like to be part of this transformation
              story. Your support, whether recurring or one-time, creates
              lasting change in communities across South Africa.
            </p>
          </div>

          {/* Donation card — no shadow, just border, matching live site */}
          <div className="rounded-2xl border bg-white p-8 sm:p-12">
            <DonationForm />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Section 5: Impact Stories ─────────────────────────────────────────────────

const IMPACT_STORIES = [
  {
    quote:
      '"I truly believe in what the team at Project Exodus is doing for the community and the world at large. The course materials are exceptional, that meetings are run with professionalism and warmth and great care is given at all times whether one is there as a facilitator, recovering from addiction or a supporter."',
    name: "Connect with Modism",
    avatar:
      "/images/testimonials/Natural_Wonders_370_Shooting_stars_wpu3nx.svg",
  },
  {
    quote:
      '"These guys are reaching people all over the world. Really making recovery treatment accessible. Love the program also, nothing else like it out there and it actually works."',
    name: "Urban Recovery SA",
    avatar:
      "/images/testimonials/Skylines_549_One_World_Trade_Center_unno2k.svg",
  },
  {
    quote:
      '"Project Exodus gives powerful life changing tools to dealing with addiction and they have a community of consistent support and accountability. I recommend their addiction recovery program it has helped me tremendously."',
    name: "Gerhardus Van Den Berg",
    avatar:
      "/images/testimonials/Waterfront_Locations_623_Diamond_Head_nrxsh8.svg",
  },
  {
    quote:
      '"It has to be one of the most exciting things that our church has run at, and we cannot wait to hear more and more God stories of wholeness and healing through our partnership with Project Exodus."',
    name: "Mark van Pletsen",
    avatar: "/images/testimonials/Forests_260_Dragon_s_blood_trees_ubnnbl.webp",
  },
  {
    quote:
      '"The project also helps to demystify mental illness, thus ensuring that the Church becomes socially relevant. On top of that, Project Exodus empowers and equips churches to be able to actively help its members."',
    name: "Maphule Ratshefola",
    avatar:
      "/images/testimonials/Skylines_541_Obelisco_de_Buenos_Aires_ldfp84.webp",
  },
] as const;

function DonateImpactStories() {
  return (
    <section className="py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 space-y-3 text-center">
          <h2 className="heading-2 font-bold">Impact Stories</h2>
          <p className="body-lg mx-auto max-w-2xl text-muted-foreground">
            Recovery thrives in communities that are properly equipped to
            address problems related to addiction. These are real testimonies
            from some of our affiliates and members.
          </p>
        </div>
      </div>

      {/* Infinite scrolling marquee with edge fades */}
      <div className="relative overflow-hidden before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-10 before:w-20 before:bg-gradient-to-r before:from-background before:to-transparent after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-10 after:w-20 after:bg-gradient-to-l after:from-background after:to-transparent max-sm:after:hidden max-sm:before:hidden sm:after:w-32 sm:before:w-32">
        <div
          className="flex w-max animate-marquee items-stretch gap-4 px-4"
          style={{ animationDuration: "50s" }}
        >
          {[...IMPACT_STORIES, ...IMPACT_STORIES].map((story, i) => (
            <div
              className="flex w-72 shrink-0 flex-col justify-between rounded-2xl border bg-card p-6 shadow-sm sm:w-80"
              // biome-ignore lint/suspicious/noArrayIndexKey: static decorative duplicate list
              key={i}
            >
              <p className="body-base">{story.quote}</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full bg-zinc-100">
                  <Image
                    alt=""
                    className="object-cover"
                    fill
                    sizes="40px"
                    src={story.avatar}
                  />
                </div>
                <span className="font-semibold text-sm">{story.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Section 6: FAQs ───────────────────────────────────────────────────────────

const FAQS = [
  {
    question: "Are donations tax-deductible?",
    answer:
      "Yes, donations made to Project Exodus are tax-deductible under South African law (Section 18A). We provide all necessary documentation for your records.",
  },
  {
    question: "How are donations used?",
    answer:
      "Contributions fund facilitator training, recovery resource development, support systems for service providers, community sponsorships, and technology infrastructure including our Bridge platform.",
  },
  {
    question: "What donation options are available?",
    answer:
      "We offer several ways to contribute: once-off donations, monthly recurring donations, annual contributions, corporate sponsorships, and in-kind donations of services or equipment.",
  },
  {
    question: "Is there a minimum donation amount?",
    answer:
      "Online donations start at R20, which helps protect our payment system from misuse. Every contribution helps us expand our reach and impact. To give more than R50,000, please contact us.",
  },
  {
    question: "Is online donation secure?",
    answer:
      "Absolutely. We use industry-standard encryption and secure payment processors (Paystack) to protect your personal and financial information.",
  },
  {
    question: "What if I have more questions about donating?",
    answer:
      "We're happy to answer any additional questions about donations or our financial operations. Please contact us at hello@projectexodus.net or call +27 82 692 3999, and our team will assist you promptly.",
  },
] as const;

function DonateFAQs() {
  return (
    <section className="py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 space-y-3">
          <h2 className="heading-2 font-bold">FAQs</h2>
          <p className="body-lg text-muted-foreground">
            Here&apos;s what you need to know about supporting our work.
          </p>
        </div>
        <FaqAccordion items={FAQS} />

        {/* Still have questions — flush with the FAQ content, no separate section */}
        <div className="mt-16 space-y-4">
          <h3 className="heading-3 font-bold">Still have questions?</h3>
          <p className="body-lg text-muted-foreground">
            We&apos;re here to assist you!
          </p>
          <div className="pt-1">
            <Button asChild variant="secondary-hybrid">
              <Link href="/contact">Contact</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Client wrapper ────────────────────────────────────────────────────────────

export function DonateClient() {
  return (
    <>
      <DonateHero />
      <DonateImpact />
      <DonateTaxBenefits />
      <DonateDonationWidget />
      <DonateImpactStories />
      <DonateFAQs />
    </>
  );
}
